import { Box, Stack, Theme, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { Children, CSSProperties, ReactNode, useMemo } from 'react';

import { Notification } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { Color, Direction, Level } from 'theme/enums';

import { ContentWrapper } from './style';

// Exported so other modules can locate the inner scroller without coupling
// to the implementation.
const ATTR = 'data-sliding-panel-scrollable-content';
export const SLIDING_PANEL_SCROLLABLE_CONTENT_ATTR = ATTR;
export const SLIDING_PANEL_SCROLLABLE_CONTENT_SELECTOR = `[${ATTR}="true"]`;

interface IProps {
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  headline?: ReactNode;
  buttons?: ReactNode;
  extra?: ReactNode;
  children?: ReactNode | ReactNode[];
  fullWidth?: boolean;
  fullScreen?: boolean;
  notificationPosition?: NotificationPosition;
  withoutNotification?: boolean;
  headerSxProps?: SxProps;
  sx?: SxProps;
  stickyHeader?: boolean;
  stickySxProps?: {
    header?: CSSProperties;
    notification?: CSSProperties;
  };
  // Bound children in a scrollable box so title + extras stay fixed (table-like).
  scrollableContent?: boolean;
}

export const SlidingPanelContent = ({
  title,
  subtitle,
  headline,
  buttons,
  extra,
  children,
  fullWidth = true,
  fullScreen = false,
  notificationPosition = NotificationPosition.DRAWER,
  withoutNotification = false,
  sx = {},
  headerSxProps = {},
  stickyHeader = false,
  stickySxProps = {
    header: {},
    notification: {},
  },
  scrollableContent = false,
}: IProps) => {
  const { notification } = useNotificationContext();

  const displayTitleSection = title || extra || subtitle;
  const displayNotification =
    !withoutNotification && notificationPosition === notification?.position;

  const titleSectionStyle: SxProps = useMemo(() => {
    if (!stickyHeader) {
      return headerSxProps;
    }

    return {
      position: 'sticky',
      top: 'calc(32px + 24px + 27px)',
      zIndex: 999,
      marginLeft: '-24px',
      marginRight: '-24px',
      marginTop: '-8px',
      backgroundColor: 'inherit',
      boxShadow:
        '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 6px 12px -2px rgba(21, 21, 21, 0.08)',
      ...(stickySxProps?.header && {
        ...stickySxProps.header,
      }),
      ...headerSxProps,
    };
  }, [headerSxProps, stickyHeader, stickySxProps]);

  const buttonsSectionStyle = useMemo(() => {
    if (!buttons || !stickyHeader) {
      return {};
    }

    const buttonsLength = Children.count(buttons);

    return {
      backgroundColor: (theme: Theme) => theme.palette.background.surface,
      display: 'flex',
      padding: '15px 24px 24px',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 999,
      marginTop: '24px',
      marginLeft: '-24px',
      bottom: 0,
      width: `calc(100% + 2 * 24px - (8px * ${buttonsLength - 1}))`,
      position: 'sticky',
      gap: '8px',
    };
  }, [buttons, stickyHeader]);

  return (
    <ContentWrapper
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      sx={{
        ...(scrollableContent && { height: '100%', minHeight: 0 }),
        ...sx,
      }}
    >
      {(displayTitleSection || displayNotification) && (
        <Stack sx={titleSectionStyle}>
          {headline}

          <Stack
            direction={Direction.COLUMN}
            sx={{ padding: displayTitleSection && stickyHeader ? '24px' : 0 }}
          >
            {(title || extra) && (
              <Stack direction={Direction.ROW} justifyContent="space-between">
                {title && <Typography level={Level.H4}>{title}</Typography>}

                {extra && <Stack>{extra}</Stack>}
              </Stack>
            )}
            {subtitle && (
              <Typography level={Level.TITLE_SM} color={Color.NEUTRAL}>
                {subtitle}
              </Typography>
            )}

            {!withoutNotification && (
              <Notification
                position={notificationPosition}
                style={{
                  ...(stickyHeader && {
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    ...stickySxProps.notification,
                  }),
                }}
              />
            )}
          </Stack>
        </Stack>
      )}

      {scrollableContent ? (
        <Box
          {...{ [SLIDING_PANEL_SCROLLABLE_CONTENT_ATTR]: 'true' }}
          sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
        >
          {children}
        </Box>
      ) : (
        children
      )}

      {buttons && <Box sx={buttonsSectionStyle}>{buttons}</Box>}
    </ContentWrapper>
  );
};

export default SlidingPanelContent;
